// HCV (Hepatitis C) view-picker.
//
// The backend pre-computes BOTH the non-HCV and HCV slices of the report's
// aggregates and ships them in a single response (top-level fields = non-HCV
// view; `hcv_view` sub-object = HCV view). The client used to re-aggregate
// from the full transactions array on every toggle — that was the dominant
// cost on iPhone Safari for multi-month combined reports, so we moved the
// computation to the server. This module now just swaps the right bundle
// into place based on the active view mode.

export const HCV_DRUG_KEYWORDS = ["EPCLUSA", "VOSEVI", "MAVIRET", "HARVONI"];

// Auto-rebate percentages negotiated directly with the manufacturer.
// Applied to drug ingredient cost = units × HCV_COST_PER_UNIT per row.
// Maviret has a lower rebate but a shorter (8-week) course; the other
// three are 12-week courses at 3%.
export const HCV_REBATE_PCT = {
  EPCLUSA: 0.03,
  VOSEVI: 0.03,
  HARVONI: 0.03,
  MAVIRET: 0.005,
};

// Fixed wholesale purchase cost per HCV bottle (4 weeks of treatment).
// All four DAA products are priced at $20,000/bottle to the pharmacy —
// the only difference is dose form (28 pills vs 84 tablets for Maviret).
// This anchor is what the user verifies their P&L against.
export const HCV_COST_PER_UNIT = 20000;

// Milestone incentive paid to the pharmacy per unique HCV client receiving
// treatment in the visible period. Flat $240 per client regardless of drug
// or payer (ODB vs NIHB) — paid ONCE per client.
export const HCV_MILESTONE_PER_CLIENT = 240;

// Aggregate milestone incentives across HCV claim rows. One $240 incentive
// per unique patient who has at least one HCV claim in the slice.
// Returns: { total, breakdown[] } where breakdown is one row per patient
// with { patient, payer, claims, drugs, amount } suitable for an audit drill.
export function aggregateMilestoneIncentives(rows) {
  const byPatient = new Map();
  for (const r of rows) {
    if (!hcvDrugKey(r.drug_name)) continue;
    const entry = byPatient.get(r.patient) || {
      patient: r.patient,
      claims: 0,
      drugs: new Set(),
      rows: [],
    };
    entry.claims += 1;
    entry.drugs.add(hcvDrugKey(r.drug_name));
    entry.rows.push(r);
    byPatient.set(r.patient, entry);
  }
  const breakdown = Array.from(byPatient.values()).map((p) => ({
    patient: p.patient,
    payer: classifyPayer(p.rows),
    claims: p.claims,
    drugs: Array.from(p.drugs),
    amount: HCV_MILESTONE_PER_CLIENT,
  }));
  return {
    total: breakdown.length * HCV_MILESTONE_PER_CLIENT,
    breakdown,
  };
}

// Return the matching HCV drug key (EPCLUSA / VOSEVI / MAVIRET / HARVONI) for
// a given drug-name string, or null if it isn't HCV.
export function hcvDrugKey(name) {
  if (!name) return null;
  const up = String(name).toUpperCase();
  return HCV_DRUG_KEYWORDS.find((k) => up.includes(k)) || null;
}

// NIHB drug-cost spread — the cash the pharmacy keeps from the difference
// between what NIHB pays as ingredient cost on a bottle versus the
// $20,000 wholesale we pay our supplier. For each NIHB row, imputed
// ingredient cost = plan1_paid − markup − net fees. Summed across all
// NIHB-classified rows, minus $20,000 × the number of distinct
// (patient × drug) bottles. ODB has zero spread (ODB pays list which
// equals our wholesale anchor, give or take rounding).
export function aggregateNihbDrugSpread(rows) {
  const byPatient = new Map();
  for (const r of rows) {
    if (!hcvDrugKey(r.drug_name)) continue;
    const arr = byPatient.get(r.patient) || [];
    arr.push(r);
    byPatient.set(r.patient, arr);
  }
  let billedTotal = 0;
  let bottleCount = 0;
  for (const [, patientRows] of byPatient) {
    if (classifyPayer(patientRows) !== "NIHB") continue;
    const drugs = new Set();
    for (const r of patientRows) {
      drugs.add(hcvDrugKey(r.drug_name));
      // No co-pay waiver on NIHB, so net fees = gross fees.
      const netFee = (r.fees || 0) > 0 ? (r.fees || 0) : 0;
      const ingredient = (r.plan1_paid || 0) - (r.markup || 0) - netFee;
      billedTotal += ingredient;
    }
    bottleCount += drugs.size;
  }
  const wholesale = bottleCount * HCV_COST_PER_UNIT;
  return +(billedTotal - wholesale).toFixed(2);
}

// Aggregate HCV purchasing rebates across rows with payer-aware anchoring,
// so the total is exact (no per-row float drift). Rules:
//   • ODB rows: each claim contributes $5,000 × rebate% (1/4 of a $20K bottle).
//   • NIHB rows: each unique (patient × drug) = 1 bottle × $20K × rebate%
//     (NIHB only permits 1-month billing → patient count, not claim count).
// Maviret rebate% = 0.5%, others = 3%.
export function aggregateHcvRebate(rows) {
  // Bucket rows by patient first so payer can be derived consistently.
  const byPatient = new Map();
  for (const r of rows) {
    if (!hcvDrugKey(r.drug_name)) continue;
    const arr = byPatient.get(r.patient) || [];
    arr.push(r);
    byPatient.set(r.patient, arr);
  }
  let total = 0;
  for (const [, patientRows] of byPatient) {
    const payer = classifyPayer(patientRows);
    if (payer === "NIHB") {
      // 1 bottle per (patient, drug) — count distinct drugs and apply
      // $20K × rebate% per bottle.
      const drugs = new Set();
      for (const r of patientRows) drugs.add(hcvDrugKey(r.drug_name));
      for (const k of drugs) {
        total += HCV_COST_PER_UNIT * (HCV_REBATE_PCT[k] || 0);
      }
    } else {
      // ODB: each claim = 1/4 of a bottle = $5,000 of drug cost.
      const claimCost = HCV_COST_PER_UNIT * unitsPerClaimForPayer("ODB"); // $5,000
      for (const r of patientRows) {
        const k = hcvDrugKey(r.drug_name);
        total += claimCost * (HCV_REBATE_PCT[k] || 0);
      }
    }
  }
  return +total.toFixed(2);
}
// Per-row auto-rebate for an HCV transaction. Returns 0 for non-HCV rows.
// Uses drug ingredient cost from `hcvDrugCostForRow` (Plan 1 basis) ×
// the manufacturer's negotiated %. Keeping the basis Plan-1-only ensures
// the audit reconciles with the ODB cycle deposit.
export function hcvRebateForRow(t) {
  const key = hcvDrugKey(t.drug_name);
  if (!key) return 0;
  return hcvDrugCostForRow(t) * (HCV_REBATE_PCT[key] || 0);
}

// Drug ingredient cost per row, derived from the Plan 1 (ODB) reimbursement
// minus the pharmacy's own markup and dispensing fee. Returns 0 for non-HCV
// rows. We deliberately use Plan 1 ONLY — not total_paid or plan2+3 — because
// the manufacturer pays rebates against the ODB-reimbursed drug cost, and the
// user verifies the math against the ODB cycle's Plan 1 deposit.
export function hcvDrugCostForRow(t) {
  if (!hcvDrugKey(t.drug_name)) return 0;
  return Math.max(0, (t.plan1_paid || 0) - (t.markup || 0) - (t.fees || 0));
}

// Consultant commissions per row (HCV only). Both consultants are paid at
// 0.5% of the HCV drug ingredient cost (~$25/week of medication). Maviret
// is an exception — only the medical consultant is paid on Maviret rows;
// the sales consultant receives nothing.
export const HCV_CONSULTANT_PCT = 0.005;
// NIHB consultant cap: each NIHB bottle (1 patient × 1 drug per cycle) earns
// at most $100 per consultant, regardless of what NIHB actually paid for it.
// Anchored to the wholesale bottle price: 0.5% × $20,000 = $100.
export const HCV_NIHB_CONSULTANT_CAP_PER_BOTTLE = HCV_COST_PER_UNIT * HCV_CONSULTANT_PCT;
export function hcvConsultantsForRow(t) {
  const key = hcvDrugKey(t.drug_name);
  if (!key) return { medical: 0, sales: 0 };
  const drugCost = hcvDrugCostForRow(t);
  const medical = drugCost * HCV_CONSULTANT_PCT;
  const sales = key === "MAVIRET" ? 0 : drugCost * HCV_CONSULTANT_PCT;
  return { medical, sales };
}

// NP consultant cap — $50 per (patient × drug) bottle/course for BOTH
// ODB and NIHB. Includes Maviret (unlike the sales consultant).
export const HCV_NP_CONSULTANT_CAP_PER_BOTTLE = 50;

// Aggregate consultant fees across a slice of HCV rows with payer-aware
// rules. Returns { medical, sales, np, breakdown[] } where breakdown has
// one row per (patient, drug) suitable for an audit drill-down.
//
// Rules:
//   • Medical: 0.5% of ingredient cost per ODB claim; NIHB capped at $100/bottle.
//   • Sales:   same as Medical but $0 on Maviret.
//   • NP:      same as Sales but additionally capped at $100/bottle for ODB too.
export function aggregateConsultantFees(rows) {
  // Group by patient to determine payer per-patient.
  const byPatient = new Map();
  for (const r of rows) {
    if (!hcvDrugKey(r.drug_name)) continue;
    const arr = byPatient.get(r.patient) || [];
    arr.push(r);
    byPatient.set(r.patient, arr);
  }
  let medical = 0;
  let sales = 0;
  let np = 0;
  const breakdown = [];
  for (const [patient, patientRows] of byPatient) {
    const payer = classifyPayer(patientRows);
    // Bucket this patient's rows by drug so each (patient, drug) = 1 bottle
    // for NIHB and so the breakdown can be drug-attributed for ODB too.
    const byDrug = new Map();
    for (const r of patientRows) {
      const k = hcvDrugKey(r.drug_name);
      if (!k) continue;
      const arr = byDrug.get(k) || [];
      arr.push(r);
      byDrug.set(k, arr);
    }
    for (const [drugKey, drugRows] of byDrug) {
      let m = 0;
      let s = 0;
      let n = 0;
      if (payer === "NIHB") {
        // Each (patient, drug) for NIHB = 1 bottle, capped at $100 per
        // consultant. Maviret pays no sales consultant, but NP IS paid
        // on Maviret at the same $100 cap.
        m = HCV_NIHB_CONSULTANT_CAP_PER_BOTTLE;
        s = drugKey === "MAVIRET" ? 0 : HCV_NIHB_CONSULTANT_CAP_PER_BOTTLE;
        n = HCV_NP_CONSULTANT_CAP_PER_BOTTLE;
      } else {
        // ODB: per-row 0.5% of ingredient cost.
        for (const r of drugRows) {
          const c = hcvConsultantsForRow(r);
          m += c.medical;
          s += c.sales;
        }
        // NP caps at $100/bottle for ODB and IS paid on Maviret too.
        // Use the medical-style 0.5% sum (which includes Maviret) for the
        // pre-cap value so a partial Maviret course pays correctly under cap.
        n = Math.min(HCV_NP_CONSULTANT_CAP_PER_BOTTLE, m);
      }
      medical += m;
      sales += s;
      np += n;
      breakdown.push({
        patient,
        drug: drugKey,
        payer,
        claims: drugRows.length,
        medical: +m.toFixed(2),
        sales: +s.toFixed(2),
        np: +n.toFixed(2),
      });
    }
  }
  return {
    medical: +medical.toFixed(2),
    sales: +sales.toFixed(2),
    np: +np.toFixed(2),
    breakdown,
  };
}

// Heuristic NIHB detection — NIHB bills an HCV course as **3 line items** per
// patient at a higher per-claim amount, whereas ODB bills 12 weekly claims at
// ~$5,250 each (or 8 for Maviret). If a patient's average per-claim Plan 1
// amount in the cycle window exceeds this threshold, we classify them as
// NIHB. A clean midpoint between the two billing patterns is $6,500.
export const NIHB_PER_CLAIM_THRESHOLD = 6500;

// Same logic as aggregateConsultantFees, but the NIHB $100/bottle cap is
// applied PER ODB PAY CYCLE rather than per HCV course. This matters when
// a patient's NIHB bottle has claims that straddle two cycles — under this
// rule the consultant earns $100 in EACH cycle the bottle touches (so a
// bottle that spans Cycle A + Cycle B pays $200 total), matching the
// per-cycle deposit reality the user audits against.
//
// payer is pre-classified using ALL the patient's visible HCV rows (across
// cycles) so the same patient can't be ODB in one cycle and NIHB in another
// due to per-slice averaging.
//
// Each breakdown entry carries a `perCycle` array attributing claims, fee,
// and payDate so callers (e.g. the consultant audit dialog) can render an
// exact per-deposit roll-up without proportional approximation.
//
// `cycles` shape: [{ key, shortLabel, dispenseStart, dispenseEnd, payDate }, ...]
// — typically `odbCyclesInRange(visibleStart, visibleEnd)`.
//
// `patientPayerMap` (optional): a Map<patientName, "ODB" | "NIHB"> built from
// the patient's FULL HCV history (not just the visible slice). If supplied,
// classification is locked to this map so the same patient is never ODB in
// one slice and NIHB in another — keeping individual-cycle pulls and grouped
// multi-cycle pulls in perfect agreement. If omitted, falls back to
// per-slice classification using the rows passed in.
export function aggregateConsultantFeesPerCycle(rows, cycles, patientPayerMap = null) {
  if (!Array.isArray(cycles) || cycles.length === 0) {
    return aggregateConsultantFees(rows);
  }
  // 1. Resolve payer per patient — externally supplied map first (stable
  //    across any slice), else fall back to slice-derived classification.
  const byPatient = new Map();
  for (const r of rows) {
    if (!hcvDrugKey(r.drug_name)) continue;
    const arr = byPatient.get(r.patient) || [];
    arr.push(r);
    byPatient.set(r.patient, arr);
  }
  const patientPayer = new Map();
  for (const [patient, patientRows] of byPatient) {
    if (patientPayerMap && patientPayerMap.has(patient)) {
      patientPayer.set(patient, patientPayerMap.get(patient));
    } else {
      patientPayer.set(patient, classifyPayer(patientRows));
    }
  }
  // 2. Bucket every HCV row by the ODB cycle that covers its service_date.
  //    Rows outside any cycle window are dropped (shouldn't happen if
  //    cycles is computed from the visible range, but safe-guard regardless).
  const cycleResolver = (iso) => {
    if (!iso) return null;
    for (const c of cycles) {
      if (iso >= c.dispenseStart && iso <= c.dispenseEnd) return c;
    }
    return null;
  };
  // 3. For each cycle, compute fees per (patient × drug) bucket and apply
  //    the NIHB cap independently.
  const breakdownByKey = new Map();
  let totalMedical = 0;
  let totalSales = 0;
  let totalNp = 0;
  for (const cycle of cycles) {
    // Collect this cycle's rows, grouped by (patient × drug).
    const byPatientDrug = new Map();
    for (const r of rows) {
      const drugKey = hcvDrugKey(r.drug_name);
      if (!drugKey) continue;
      if (cycleResolver(r.service_date)?.key !== cycle.key) continue;
      const pkKey = `${r.patient}||${drugKey}`;
      const arr = byPatientDrug.get(pkKey) || {
        patient: r.patient,
        drug: drugKey,
        rows: [],
      };
      arr.rows.push(r);
      byPatientDrug.set(pkKey, arr);
    }
    for (const [pkKey, group] of byPatientDrug) {
      const payer = patientPayer.get(group.patient) || "ODB";
      let m = 0;
      let s = 0;
      let n = 0;
      if (payer === "NIHB") {
        m = HCV_NIHB_CONSULTANT_CAP_PER_BOTTLE;
        s = group.drug === "MAVIRET" ? 0 : HCV_NIHB_CONSULTANT_CAP_PER_BOTTLE;
        n = HCV_NP_CONSULTANT_CAP_PER_BOTTLE;
      } else {
        for (const r of group.rows) {
          const c = hcvConsultantsForRow(r);
          m += c.medical;
          s += c.sales;
        }
        n = Math.min(HCV_NP_CONSULTANT_CAP_PER_BOTTLE, m);
      }
      totalMedical += m;
      totalSales += s;
      totalNp += n;
      const existing = breakdownByKey.get(pkKey) || {
        patient: group.patient,
        drug: group.drug,
        payer,
        claims: 0,
        medical: 0,
        sales: 0,
        np: 0,
        perCycle: [],
      };
      existing.claims += group.rows.length;
      existing.medical += m;
      existing.sales += s;
      existing.np += n;
      existing.perCycle.push({
        cycleKey: cycle.key,
        cycleLabel: cycle.shortLabel,
        payDate: cycle.payDate,
        claims: group.rows.length,
        medical: +m.toFixed(2),
        sales: +s.toFixed(2),
        np: +n.toFixed(2),
      });
      breakdownByKey.set(pkKey, existing);
    }
  }
  const breakdown = Array.from(breakdownByKey.values()).map((b) => ({
    ...b,
    medical: +b.medical.toFixed(2),
    sales: +b.sales.toFixed(2),
    np: +b.np.toFixed(2),
  }));
  return {
    medical: +totalMedical.toFixed(2),
    sales: +totalSales.toFixed(2),
    np: +totalNp.toFixed(2),
    breakdown,
  };
}

// "Combined" gross billing per row, with the HCV/PSP rule baked in.
// For HCV drugs where Plan 2 (or Pat/Ded) has a non-zero amount, that
// payment is funded by the Patient Support Program (PSP) — it's NOT
// additional pharmacy revenue, it's the patient's deductible being paid
// by PSP and should be SUBTRACTED from Plan 1 instead of added. For all
// other rows, Combined is the usual Plan 1 + Plan 2.
export function pspAmountForRow(row) {
  // Prefer Pat/Ded Paid (the deductible column) when present — HCV PSP
  // deductibles typically populate this column, not the true Plan 2 slot.
  // Fall back to plan2_paid so historically-parsed reports still work.
  return (row?.pat_ded_paid || 0) || (row?.plan2_paid || 0);
}

export function combinedAmountForRow(row) {
  const p1 = row?.plan1_paid || 0;
  const p2 = row?.plan2_paid || 0;
  const psp = pspAmountForRow(row);
  if (psp > 0 && hcvDrugKey(row.drug_name)) return p1 - psp;
  return p1 + p2;
}

// True if a row contributes to the PSP Reimbursement tab (HCV + PSP > 0).
export function isPspRow(row) {
  return pspAmountForRow(row) > 0 && !!hcvDrugKey(row?.drug_name);
}

// Build a stable Patient → "ODB" | "NIHB" map from a *complete* set of HCV
// rows (typically every HCV transaction in the report). Pass the resulting
// map into `aggregateConsultantFeesPerCycle` so a patient's payer class
// stays the same whether you're viewing one cycle or all of them.
export function buildPatientPayerMap(allRows) {
  const byPatient = new Map();
  for (const r of allRows || []) {
    if (!hcvDrugKey(r.drug_name)) continue;
    const arr = byPatient.get(r.patient) || [];
    arr.push(r);
    byPatient.set(r.patient, arr);
  }
  const map = new Map();
  for (const [patient, patientRows] of byPatient) {
    map.set(patient, classifyPayer(patientRows));
  }
  return map;
}

// Classify a patient's HCV claims as ODB or NIHB.
//
// Prefers the authoritative `billed_to` column from the PDF when
// available:
//   • Billed To = "IA"  → NIHB (Indigenous Affairs / federal NIHB)
//   • Billed To = "ODB" → ODB
//   • CSH / other cash → ignored for classification (fall through)
// Falls back to the per-claim-average heuristic when billed_to is
// missing (older parses or unusual rows).
//
// Non-HCV rows always come back as ODB.
export function classifyPayer(patientRows) {
  if (!Array.isArray(patientRows) || patientRows.length === 0) return "ODB";
  const isHcv = patientRows.some((r) => hcvDrugKey(r.drug_name));
  if (!isHcv) return "ODB";
  // 1. Authoritative signal: Billed To on plan-paid rows.
  let hasIA = false;
  let hasODB = false;
  let planPaidRows = 0;
  let planPaidTotal = 0;
  for (const r of patientRows) {
    const bt = (r.billed_to || "").toUpperCase();
    const plan1 = r.plan1_paid || 0;
    if (plan1 > 0) {
      if (bt === "IA") hasIA = true;
      else if (bt === "ODB") hasODB = true;
      planPaidRows += 1;
      planPaidTotal += plan1;
    }
  }
  if (hasIA && !hasODB) return "NIHB";
  if (hasODB && !hasIA) return "ODB";
  if (hasIA && hasODB) {
    // Mixed billing (rare) — fall back to average per-plan-paid-claim.
    return planPaidRows > 0 && planPaidTotal / planPaidRows > NIHB_PER_CLAIM_THRESHOLD
      ? "NIHB"
      : "ODB";
  }
  // 2. Legacy fallback: no billed_to captured. Use avg per-claim heuristic.
  const totalPlan1 = patientRows.reduce((s, r) => s + (r.plan1_paid || 0), 0);
  const avgPerClaim = totalPlan1 / patientRows.length;
  return avgPerClaim > NIHB_PER_CLAIM_THRESHOLD ? "NIHB" : "ODB";
}

// Units (bottles) per claim depend on the payer.
//   • ODB courses are 4 weekly claims per bottle → 1/4 bottle per claim.
//   • NIHB only permits ONE bottle (one month's supply) per claim;
//     bottles can never be split into fractions like 0.75 / 1.75 / 0.33.
//     One NIHB claim = one full bottle of drug cost.
export function unitsPerClaimForPayer(payer) {
  return payer === "NIHB" ? 1 : 1 / 4;
}

export function isHcvDrug(name) {
  if (!name) return false;
  const up = String(name).toUpperCase();
  return HCV_DRUG_KEYWORDS.some((k) => up.includes(k));
}

// Collapse minor name variants (e.g., a leaked patient first name prepended
// during PDF parsing — "DANA VOSEVI") down to the canonical HCV drug keyword
// so the breakdown doesn't show ghost drugs.
export function canonicalDrugName(name) {
  if (!name) return "";
  const up = String(name).toUpperCase().trim();
  const match = HCV_DRUG_KEYWORDS.find((k) => up.includes(k));
  return match || up;
}

const EMPTY_AGG = {
  num_days: 0,
  total_dispensings: 0,
  total_fees: 0,
  total_markup: 0,
  avg_daily_fees: 0,
  monthly_revenue_est: 0,
  annual_revenue_est: 0,
  date_range_start: null,
  date_range_end: null,
  per_day: [],
  zero_fee_entries: [],
  drug_breakdown_per_day: {},
  drug_breakdown_total: [],
  per_month_summary: [],
};

/**
 * Return the aggregate bundle for the requested view, merging in any common
 * top-level fields and the (optionally inlined) transactions array.
 *
 * @param {object} original - full ReportAnalysis from the backend
 * @param {"non-hcv"|"hcv"} viewMode
 */
export function recomputeAnalysis(original, viewMode) {
  if (!original) {
    return { filename: "", transactions: [], ...EMPTY_AGG };
  }
  if (viewMode === "hcv") {
    const hcvAgg = original.hcv_view || EMPTY_AGG;
    const allTx = original.transactions || [];
    return {
      filename: original.filename,
      ...hcvAgg,
      transactions: allTx.filter((t) => isHcvDrug(t.drug_name)),
      hcv_view: original.hcv_view,
    };
  }
  // Non-HCV view = top-level aggregates (the backend ships the non-HCV slice
  // there directly). Filter the transactions client-side to match.
  const allTx = original.transactions || [];
  return {
    ...original,
    transactions: allTx.filter((t) => !isHcvDrug(t.drug_name)),
  };
}
